import {
  FormSubmitButton,
  FormCivilDatePicker,
  FormTextField,
  FormProvider,
  useForm,
} from '@mfw/forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntl, Button } from '@mfw/components';
import Box from '@mui/material/Box';

import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';
import type { UiUserAboutStep } from '../../../../../schemas/users.schema';

import { UiUserAboutStepSchema } from '../../../../../schemas/users.schema';
import { useUpdateUser } from '../../../../../hooks/usersv2/useUpdateUser';
import OnboardingHeader from '../../../OnboardingHeader';
import { useAuthContext } from '../../../../../context';
import { GenderField } from '../../../../../allFeatures/GenderField/GenderField';
import { useWizard } from '../../../../../allFeatures';
import generalMessages from '../../../../../messages';
import messages from '../AboutStep/messages';
import { aboutStepDefaults } from '../aboutStepDefaults';
import config from '../../../../../config';

export const AboutDialogStep = () => {
  const { formatMessage } = useIntl();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const { mutateAsync: updateUserV2Async } = useUpdateUser();
  const { currentUser } = useAuthContext();

  const methods = useForm({
    defaultValues: aboutStepDefaults(currentUser),
    resolver: zodResolver(UiUserAboutStepSchema),
  });

  const handleNext = async (data: UiUserAboutStep) => {
    await updateUserV2Async({
      fitnessProfile: {
        height: data.height,
        weight: data.weight,
      },
      userSettings: {
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
      },
    });
    goToStep('trainingLevel');
  };

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={<p>{formatMessage(messages.description)}</p>}
          title={formatMessage(messages.title)}
        />
      </Box>
      <Box
        sx={{
          '& .Mfw-Autocomplete-inputLabel__wrapper': {
            backgroundColor: 'background.paper',
          },
        }}
        flexDirection="column"
        display="flex"
        gap={2}
        px={3}
        py={2}
      >
        <FormCivilDatePicker
          maxDate={config.onboarding.miniumDateOfBirthDate}
          helperText={formatMessage(messages.ageHelper)}
          data-testid="client-onboarding-date-of-birth"
          label={formatMessage(messages.age)}
          name="dateOfBirth"
          className="my-4"
          fullWidth
        />
        <GenderField data-testid="client-onboarding-gender" name="gender" />
        <FormTextField
          label={formatMessage(messages.height)}
          data-testid="client-onboarding-height"
          className="my-4"
          name="height"
          type="number"
        />
        <FormTextField
          label={formatMessage(messages.weight)}
          data-testid="client-onboarding-weight"
          name="weight"
          type="number"
        />
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          onClick={() => goToStep('basicInfo')}
          data-testid="onboarding-back"
          variant="outlined"
          size="large"
        >
          {formatMessage(generalMessages.back)}
        </Button>
        <FormSubmitButton
          data-testid="onboarding-next"
          variant="contained"
          disableIfNotDirty
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};
